import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Lightning, CreditCard, Warning, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function AutoDistributeCardsDialog({
  open, onOpenChange, fileId, totalBookings, ratePerCylinder,
  defaultGroupSize = 5, agencyId = null, onApplied,
}) {
  const [step, setStep] = useState('config'); // config | preview | applied
  const [preBalance, setPreBalance] = useState(0);
  const [groupSize, setGroupSize] = useState(defaultGroupSize);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const computePlan = async () => {
    setBusy(true);
    try {
      const r = await api.post('/card-distribution/plan', {
        file_id: fileId,
        total_bookings: Number(totalBookings),
        rate_per_cylinder: Number(ratePerCylinder),
        group_size: Number(groupSize),
        account_prebalance: Number(preBalance),
        agency_id: agencyId,
      });
      setPlan(r.data);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Plan compute failed');
    } finally { setBusy(false); }
  };

  const applyPlan = async () => {
    if (!plan?.allocations?.length) { toast.error('No allocations'); return; }
    setBusy(true);
    try {
      const r = await api.post('/card-distribution/apply', {
        file_id: fileId,
        allocations: plan.allocations,
        account_prebalance: Number(preBalance),
      });
      toast.success(r.data.message);
      setStep('applied');
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setStep('config'); setPlan(null); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="auto-distribute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightning size={18} className="text-violet-600" weight="fill" />
            Auto-Distribute Cards
          </DialogTitle>
        </DialogHeader>

        {step === 'config' && (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              <div className="font-bold text-blue-900 mb-1">File Details</div>
              <div>Total bookings: <b>{totalBookings}</b></div>
              <div>Rate per cylinder: <b>{fmt(ratePerCylinder)}</b></div>
              <div>Total amount: <b>{fmt(totalBookings * ratePerCylinder)}</b></div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Account Pre-Balance (optional)</label>
              <div className="text-[11px] text-gray-500 mb-1">Amazon/TataNeu account me pehle se kitna balance hai? (rewards, gift cards, etc.)</div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">₹</span>
                <Input
                  type="number" value={preBalance}
                  onChange={(e) => setPreBalance(Number(e.target.value) || 0)}
                  className="h-9 text-sm" placeholder="0"
                  data-testid="prebalance-input"
                />
              </div>
              {preBalance > 0 && ratePerCylinder > 0 && (
                <div className="text-[11px] text-emerald-700 mt-1">
                  ≈ {Math.floor(preBalance / ratePerCylinder)} bookings pre-balance se ho jayenge (no card debit).
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Group Size</label>
              <div className="text-[11px] text-gray-500 mb-1">Ek card me ek saath kitni bookings (per group)?</div>
              <select
                value={groupSize}
                onChange={(e) => setGroupSize(Number(e.target.value))}
                className="h-9 text-sm border rounded px-2 w-24"
                data-testid="groupsize-select"
              >
                {Array.from({ length: 22 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-[11px] text-gray-500 ml-2">
                Group amount: {fmt(groupSize * ratePerCylinder)}
              </span>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={computePlan} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="compute-plan-btn">
                {busy ? 'Computing…' : 'Compute Plan'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'preview' && plan && (
          <div className="space-y-3">
            <div className={`border rounded p-3 text-sm ${plan.sufficient ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
              <div className="flex items-center gap-2">
                {plan.sufficient ? (
                  <><CheckCircle size={16} weight="fill" className="text-emerald-700" /><span className="font-bold">All {plan.total_bookings} bookings covered!</span></>
                ) : (
                  <><Warning size={16} weight="fill" className="text-amber-700" /><span className="font-bold">{plan.bookings_covered}/{plan.total_bookings} covered · {plan.bookings_uncovered} uncovered</span></>
                )}
              </div>
              <div className="text-[11px] mt-1">
                Total: {fmt(plan.total_amount)} · Pre-balance: {fmt(plan.account_prebalance)} · Card-load: {fmt(plan.amount_needed_from_cards)}
              </div>
            </div>

            <div className="bg-white border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left">Source</th>
                    <th className="px-2 py-2 text-right">Bookings</th>
                    <th className="px-2 py-2 text-right">Amount</th>
                    <th className="px-2 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.allocations.map((a, i) => (
                    <tr key={i} className="border-t" data-testid={`alloc-row-${i}`}>
                      <td className="px-2 py-1">{i + 1}</td>
                      <td className="px-2 py-1">
                        {a.source === 'prebalance' ? (
                          <span className="text-emerald-700 font-bold flex items-center gap-1">🎁 Pre-Balance</span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <CreditCard size={12} className="text-blue-600" />
                            <span className="font-mono">****{a.card_last4}</span>
                            {a.card_linked_agency_name && (
                              <span className="text-[9px] bg-violet-100 text-violet-800 px-1 rounded">🔒 {a.card_linked_agency_name}</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right font-bold">{a.bookings}</td>
                      <td className="px-2 py-1 text-right">{fmt(a.amount)}</td>
                      <td className="px-2 py-1 text-[10px] text-gray-500">
                        {a.groups > 0 ? `${a.groups} full groups` : ''}
                        {a.residual_bookings > 0 ? ` + ${a.residual_bookings} residual` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {plan.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px]" data-testid="plan-warnings">
                <div className="font-bold text-amber-800 mb-1">⚠️ Warnings:</div>
                {plan.warnings.map((w, i) => <div key={i} className="text-amber-700">• {w}</div>)}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('config')} disabled={busy}>← Back</Button>
              <Button
                onClick={applyPlan} disabled={busy || plan.allocations.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="apply-plan-btn"
              >
                {busy ? 'Applying…' : `Apply Plan (${plan.bookings_covered} rows)`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'applied' && (
          <div className="py-6 text-center space-y-3">
            <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-600" />
            <div className="text-lg font-bold">Cards distributed!</div>
            <div className="text-sm text-gray-600">Rows ko respective cards ke sath stamp kar diya.</div>
            <Button onClick={close} className="bg-violet-600 hover:bg-violet-700">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
